import {
  BaseEdge,
  EdgeLabelRenderer,
  useReactFlow,
  type EdgeProps,
} from '@xyflow/react';
import { WarningIcon, XIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import type { DraftState } from 'src/lib/workflows/workflowDraftOverlay';
import { useZipTieRoutedEdge } from 'src/components/workflows/zipTieRouting';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

// fallow-ignore-next-line complexity
export function DeletableEdge(props: EdgeProps) {
  const { id, style = {}, markerEnd, selected, data } = props;
  const gt = useGT();
  const { deleteElements } = useReactFlow();
  const {
    path: edgePath,
    labelX,
    labelY,
    bundled,
  } = useZipTieRoutedEdge(props);

  const draftState = (data as { __draftState?: DraftState } | undefined)
    ?.__draftState;
  const isDraftRemoved = draftState === 'removed';
  // `editable` is injected by the editor to match the canvas-wide read-only
  // gate. Default to `true` so surfaces that don't set it (e.g. the groups
  // editor) stay interactive — read-only contexts must opt in explicitly.
  const isEditable =
    (data as { editable?: boolean } | undefined)?.editable ?? true;

  // Sticky outline from the last save/publish validation attempt — persists
  // until the user's next edit, independent of the hover-highlight below.
  const hasSaveValidationError =
    (data as { hasSaveValidationError?: boolean } | undefined)
      ?.hasSaveValidationError ?? false;
  const saveValidationErrorMessage = (
    data as { saveValidationErrorMessage?: string } | undefined
  )?.saveValidationErrorMessage;

  const onEdgeClick = () => {
    if (isDraftRemoved || !isEditable) return;
    void deleteElements({ edges: [{ id }] });
  };

  // AI-49: when an edge is part of a draft overlay, let its overlay-provided
  // `style` (stroke/dash/opacity) win over the default selection styling.
  // A validation error always wins, regardless of hover/draft styling.
  const baseStroke = selected ? '#3b82f6' : bundled ? '#2563eb' : '#94a3b8';
  const finalStroke = hasSaveValidationError
    ? '#dc2626'
    : (style.stroke ?? baseStroke);
  const finalStrokeWidth = hasSaveValidationError
    ? 3
    : (style.strokeWidth ?? (selected || bundled ? 3 : 2));

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeWidth: finalStrokeWidth,
          stroke: finalStroke,
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan flex items-center gap-1"
        >
          {hasSaveValidationError && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-5 h-5 rounded-full bg-red-600 border-2 border-white dark:border-slate-900 flex items-center justify-center shadow-md cursor-default">
                  <WarningIcon className="w-3 h-3 text-white" weight="fill" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {saveValidationErrorMessage ??
                  gt('This connection has a validation error')}
              </TooltipContent>
            </Tooltip>
          )}
          {isEditable && (
            <button
              type="button"
              onClick={onEdgeClick}
              className={`
              w-5 h-5 rounded-full
              bg-white dark:bg-slate-800
              border-2 border-slate-300 dark:border-slate-600
              hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/30
              flex items-center justify-center
              transition-all duration-150
              opacity-0 hover:opacity-100
              ${selected ? 'opacity-100 border-blue-500' : ''}
              group
            `}
              title={gt('Delete connection', {
                context: 'workflow graph edge, remove link between steps',
              })}
            >
              <XIcon className="w-3 h-3 text-slate-400 group-hover:text-red-500" />
            </button>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
